import i18n from 'i18next';
import sanitizeHtml from 'sanitize-html';
import PropTypes from 'prop-types';

import moment from 'moment';
import 'moment/min/locales';

const LANGUAGES = {
    /**
     * Make sure the locale code is supported:
     * https://uilibrary.hq.eset.com/#/documentation/locales
     * https://momentjs.com/
     */
    'en-US': {
        label: 'ENGLISH',
        value: window.serverInfo.constants.supportedLanguages.EN_US,
        valueForHtml: 'en',
        supported: true,
        translation: async () => await import('Assets/translations/en-US.json'),
        locale: 'en-US',
    },
    'ja-JP': {
        label: 'Japanese',
        value: window.serverInfo.constants.supportedLanguages.JA_JP,
        valueForHtml: 'ja',
        supported: true,
        translation: async () => await import('Assets/translations/ja-JP.json'),
        locale: 'ja',
    },
    'uk-UA': {
        label: 'Ukrainian',
        value: window.serverInfo.constants.supportedLanguages.UK_UA,
        valueForHtml: 'uk',
        supported: true,
        translation: async () => await import('Assets/translations/uk-UA.json'),
        locale: 'uk',
    },
    'zh-CN': {
        label: 'Chinese',
        value: window.serverInfo.constants.supportedLanguages.ZH_CN,
        valueForHtml: 'zh',
        supported: true,
        translation: async () => await import('Assets/translations/zh-CN.json'),
        locale: 'zh-CN',
    },
};

const NOT_TRANSLATABLE_NAME = 'default';

function getLanguageByValue(valueForLanguage) {
    const languageEntry = Object.entries(LANGUAGES).find((language) => {
        const languageProperties = language[1];
        return languageProperties.value === valueForLanguage;
    });
    return languageEntry[0];
}

function isLanguageSupported(languageCode) {
    if (languageCode in LANGUAGES) {
        return LANGUAGES[languageCode].supported;
    }
    return false;
}

function getDefaultLanguage() {
    return getLanguageByValue(window.serverInfo.constants.defaultLanguage);
}

const DEFAULT_LANGUAGE = getDefaultLanguage();

function getStoredLanguage() {
    const storedLanguage = window.localStorage.getItem('language');
    return storedLanguage && isLanguageSupported(storedLanguage) ? storedLanguage : DEFAULT_LANGUAGE;
}

function setLanguageInHtmlDocument(language) {
    document.documentElement.setAttribute('lang', LANGUAGES[language].valueForHtml);
}

const loadTranslation = async (language) => {
    try {
        return await LANGUAGES[language].translation?.();
    } catch (error) {
        console.error('Loading translation failed for language:', language);
    }
};

async function generateResources(lng) {
    const language = isLanguageSupported(lng) ? lng : DEFAULT_LANGUAGE;
    const translation = await loadTranslation(language);
    return { [lng]: { translation } };
}

async function init(lng = DEFAULT_LANGUAGE) {
    setLanguageInHtmlDocument(lng);
    moment.locale(LANGUAGES[lng].locale);
    window.localStorage.setItem('language', lng);

    const resources = await generateResources(lng);

    return i18n.init({
        resources,
        react: {
            transKeepBasicHtmlNodesFor: ['br', 'em', 'i', 'strong', 'p', 'b'],
        },
        lng,
        fallbackLng: DEFAULT_LANGUAGE,
    });
}

function t(template, context = {}) {
    // Way of using i18next library in this code causes that 'default' as key for translation
    // returns whole translation namespace. Adopting code to newest standards to avoid it
    // would require quite big refactor of code and might cause bigger problems on the way.
    // Due to this and the fact that i18next library is not dedicated for React it is better for now
    // to just not translate 'default' key to avoid appearance of bug in UI
    if (typeof template !== 'string' || !i18n.exists(template, context) || template === NOT_TRANSLATABLE_NAME) {
        return template;
    }
    return i18n.t(template, context);
}

function getResources() {
    return i18n.options?.resources;
}

function HtmlTranslation(props) {
    function createTranslation() {
        const context = props.i18nContext ? props.i18nContext : {};
        return { __html: sanitizeHtml(t(props.i18nKey, context)) };
    }
    return <span dangerouslySetInnerHTML={createTranslation()} />;
}

function i18nFormattedTranslation(eiTagName, eiAttributeName, FormatterComponent, template, context) {
    const translation = t(template, context);
    const splitedByEiTagName = translation.split(new RegExp(`{${eiTagName}|{/${eiTagName}}`, 'g'));
    return splitedByEiTagName.filter(Boolean).map((value, id) => {
        const eiAttributeValue = value.match(new RegExp(`(?<=${eiAttributeName}=')(.*)(?='})`, 'g'))?.[0];
        return eiAttributeValue ? (
            <FormatterComponent
                key={id}
                tagValue={eiAttributeValue}
                value={value.match(new RegExp(`(?<='})(.*)`, 'g'))?.[0]}
            />
        ) : (
            value
        );
    });
}

HtmlTranslation.propTypes = {
    i18nKey: PropTypes.string.isRequired,
};

export {
    init,
    HtmlTranslation,
    getResources,
    i18nFormattedTranslation,
    getStoredLanguage,
    getLanguageByValue,
    setLanguageInHtmlDocument,
    LANGUAGES,
    DEFAULT_LANGUAGE,
};
export default t;
